import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './NotificationPanel.css';

const FILTER_ALL = 'all';
const FILTER_UNREAD = 'unread';

function formatTimestamp(value) {
  if (!value) {
    return '';
  }

  const created = new Date(value);
  if (Number.isNaN(created.getTime())) {
    return '';
  }

  const diffMs = Date.now() - created.getTime();
  const diffMinutes = Math.max(Math.floor(diffMs / 60000), 0);
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return created.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function isSameLocalDate(left, right) {
  return (
    left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate()
  );
}

function groupNotifications(notifications) {
  const now = new Date();
  const groups = { today: [], earlier: [] };

  notifications.forEach((notification) => {
    const created = new Date(notification.createdAt);
    if (!Number.isNaN(created.getTime()) && isSameLocalDate(created, now)) {
      groups.today.push(notification);
      return;
    }
    groups.earlier.push(notification);
  });

  return groups;
}

function getNotificationTone(type) {
  switch (type) {
    case 'new_follower':
      return 'follower';
    case 'new_subscriber':
    case 'subscription_reactivated':
      return 'follower';
    case 'new_media_submission':
      return 'media';
    case 'submission_approved':
      return 'approved';
    case 'subscription_cancelled':
    case 'submission_rejected':
      return 'rejected';
    case 'media_shown':
      return 'shown';
    default:
      return 'general';
  }
}

/**
 * Notification dropdown panel.
 *
 * Displays a scrollable list of notifications when the bell icon is clicked.
 * Shows an empty state when there are no notifications.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the panel is visible
 * @param {function} props.onClose - Handler to close the panel
 * @param {Array} props.notifications - Array of notification objects
 * @returns {JSX.Element|null}
 */
function NotificationPanel({
  isOpen,
  notifications,
  unreadCount = 0,
  isLoading = false,
  errorMessage = '',
  onClose,
  onClearAll,
  onNotificationClick,
}) {
  const [activeFilter, setActiveFilter] = useState(FILTER_ALL);
  const unreadItems = useMemo(
    () => notifications.filter((notification) => !notification.isRead),
    [notifications]
  );
  const filteredNotifications = useMemo(
    () => (
      activeFilter === FILTER_UNREAD
        ? unreadItems
        : notifications
    ),
    [activeFilter, notifications, unreadItems]
  );
  const groupedNotifications = useMemo(
    () => groupNotifications(filteredNotifications),
    [filteredNotifications]
  );
  const hasVisibleNotifications = filteredNotifications.length > 0;
  const hasAnyNotifications = notifications.length > 0;

  if (!isOpen) return null;

  return (
    <div className="notification-panel" role="dialog" aria-label="Notifications">
      <div className="notification-panel__header">
        <div className="notification-panel__heading">
          <div>
            <h3 className="notification-panel__title">Notifications</h3>
            <p className="notification-panel__subtitle">
              {unreadCount > 0 ? `${unreadCount} unread` : 'You are all caught up'}
            </p>
          </div>
          {unreadCount > 0 && (
            <span className="notification-panel__count-pill">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </div>
        <div className="notification-panel__actions">
          <button
            type="button"
            className="notification-panel__clear"
            onClick={onClearAll}
            disabled={!hasAnyNotifications || isLoading}
          >
            Clear all
          </button>
          <button
            type="button"
            className="notification-panel__close"
            onClick={onClose}
            aria-label="Close notifications"
          >
            x
          </button>
        </div>
      </div>
      <div className="notification-panel__filters" aria-label="Notification filters">
        <button
          type="button"
          className={`notification-panel__filter-chip ${activeFilter === FILTER_ALL ? 'notification-panel__filter-chip--active' : ''}`}
          onClick={() => setActiveFilter(FILTER_ALL)}
          aria-pressed={activeFilter === FILTER_ALL}
        >
          All
          <span className="notification-panel__filter-count">{notifications.length}</span>
        </button>
        <button
          type="button"
          className={`notification-panel__filter-chip ${activeFilter === FILTER_UNREAD ? 'notification-panel__filter-chip--active' : ''}`}
          onClick={() => setActiveFilter(FILTER_UNREAD)}
          aria-pressed={activeFilter === FILTER_UNREAD}
        >
          Unread
          <span className="notification-panel__filter-count">{unreadItems.length}</span>
        </button>
      </div>
      <div className="notification-panel__content">
        {isLoading ? (
          <div className="notification-panel__state">
            <span className="notification-panel__state-icon notification-panel__state-icon--loading" aria-hidden="true">
              <StatusSparkIcon />
            </span>
            <p className="notification-panel__empty-title">Loading notifications...</p>
            <span className="notification-panel__empty-desc">
              Checking your latest activity.
            </span>
          </div>
        ) : errorMessage ? (
          <div className="notification-panel__state">
            <span className="notification-panel__state-icon notification-panel__state-icon--error" aria-hidden="true">
              <StatusAlertIcon />
            </span>
            <p className="notification-panel__empty-title">{errorMessage}</p>
            <span className="notification-panel__empty-desc">
              Try opening notifications again in a moment.
            </span>
          </div>
        ) : !hasVisibleNotifications ? (
          <div className="notification-panel__state">
            <span className="notification-panel__state-icon" aria-hidden="true">
              {activeFilter === FILTER_UNREAD ? <StatusCheckIcon /> : <StatusSparkIcon />}
            </span>
            <p className="notification-panel__empty-title">
              {activeFilter === FILTER_UNREAD ? 'No unread notifications' : 'No notifications at this time'}
            </p>
            <span className="notification-panel__empty-desc">
              {activeFilter === FILTER_UNREAD
                ? 'Everything in your inbox has already been reviewed.'
                : 'When you receive a notification, it will show up here.'}
            </span>
          </div>
        ) : (
          <div className="notification-panel__list">
            {groupedNotifications.today.length > 0 && (
              <NotificationGroup
                title="Today"
                notifications={groupedNotifications.today}
                onNotificationClick={onNotificationClick}
              />
            )}
            {groupedNotifications.earlier.length > 0 && (
              <NotificationGroup
                title="Earlier"
                notifications={groupedNotifications.earlier}
                onNotificationClick={onNotificationClick}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function NotificationGroup({ title, notifications, onNotificationClick }) {
  return (
    <section className="notification-panel__group" aria-label={title}>
      <div className="notification-panel__group-header">
        <span className="notification-panel__group-title">{title}</span>
      </div>
      <div className="notification-panel__group-items">
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onClick={() => onNotificationClick?.(notification)}
          />
        ))}
      </div>
    </section>
  );
}

function NotificationItem({ notification, onClick }) {
  const {
    type,
    title,
    body,
    createdAt,
    isRead,
    actorUsername,
    actorProfileImage,
  } = notification;
  const tone = getNotificationTone(type);

  return (
    <button
      type="button"
      className={`notification-item notification-item--${tone} ${!isRead ? 'notification-item--unread' : ''}`}
      onClick={onClick}
    >
      <div className="notification-item__leading">
        <NotificationAvatar
          username={actorUsername}
          profileImage={actorProfileImage}
        />
        <span className={`notification-item__type-icon notification-item__type-icon--${tone}`} aria-hidden="true">
          <NotificationTypeIcon type={type} />
        </span>
      </div>
      <div className="notification-item__content">
        <p className="notification-item__title">{title}</p>
        <p className="notification-item__text">{body}</p>
        <span className="notification-item__time">{formatTimestamp(createdAt)}</span>
      </div>
      {!isRead && <span className="notification-item__dot" />}
    </button>
  );
}

function NotificationTypeIcon({ type }) {
  switch (type) {
    case 'new_follower':
      return (
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M10 10.1a3.3 3.3 0 1 0 0-6.6 3.3 3.3 0 0 0 0 6.6Z" stroke="currentColor" strokeWidth="1.6" />
          <path d="M4.4 16c1.1-2.2 3-3.3 5.6-3.3 2.7 0 4.6 1.1 5.7 3.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M15.8 4.2v4.2M13.7 6.3H18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case 'new_media_submission':
      return (
        <svg viewBox="0 0 20 20" fill="none">
          <rect x="3.1" y="4.1" width="13.8" height="11.8" rx="2.2" stroke="currentColor" strokeWidth="1.6" />
          <path d="m7.1 12.5 2-2.3 2.1 1.7 2.2-2.8 2.2 3.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="7.4" cy="7.8" r="1.1" fill="currentColor" />
        </svg>
      );
    case 'new_subscriber':
      return (
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M10 3.6 11.8 7l3.8.5-2.8 2.6.7 3.8L10 12l-3.5 1.9.7-3.8-2.8-2.6L8.2 7 10 3.6Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M14.8 14.5v2.4M13.6 15.7H16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case 'submission_approved':
      return (
        <svg viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="6.4" stroke="currentColor" strokeWidth="1.6" />
          <path d="m7.3 10.2 1.8 1.9 3.7-3.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'submission_rejected':
      return (
        <svg viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="6.4" stroke="currentColor" strokeWidth="1.6" />
          <path d="m7.8 7.8 4.4 4.4M12.2 7.8l-4.4 4.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'subscription_cancelled':
      return (
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M10 3.6 11.8 7l3.8.5-2.8 2.6.7 3.8L10 12l-3.5 1.9.7-3.8-2.8-2.6L8.2 7 10 3.6Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M13.6 15.8H16.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'media_shown':
      return (
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M3.6 10c1.9-3 4.1-4.5 6.4-4.5s4.5 1.5 6.4 4.5c-1.9 3-4.1 4.5-6.4 4.5S5.5 13 3.6 10Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="10" cy="10" r="2" fill="currentColor" />
        </svg>
      );
    case 'subscription_reactivated':
      return (
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M10 3.6 11.8 7l3.8.5-2.8 2.6.7 3.8L10 12l-3.5 1.9.7-3.8-2.8-2.6L8.2 7 10 3.6Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M13.6 16.1a1.9 1.9 0 1 0 1.7-2.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M15.9 12.7v1.6h-1.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M10 3.5a4 4 0 0 0-4 4V9c0 .8-.2 1.5-.7 2.1l-1 1.4h11.4l-1-1.4A3.3 3.3 0 0 1 14 9V7.5a4 4 0 0 0-4-4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M8.5 14.2a1.7 1.7 0 0 0 3 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
  }
}

function NotificationAvatar({ username, profileImage }) {
  const [imageFailed, setImageFailed] = useState(false);
  const fallbackText = username?.trim()?.charAt(0)?.toUpperCase() || 'PA';

  return (
    <div className="notification-item__avatar" aria-hidden="true">
      {profileImage && !imageFailed ? (
        <img
          src={profileImage}
          alt=""
          className="notification-item__avatar-image"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="notification-item__avatar-fallback">{fallbackText}</span>
      )}
    </div>
  );
}

function StatusSparkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M12 3.5v4.1M12 16.4v4.1M20.5 12h-4.1M7.6 12H3.5M17.7 6.3l-2.9 2.9M9.2 14.8l-2.9 2.9M17.7 17.7l-2.9-2.9M9.2 9.2 6.3 6.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="12" cy="12" r="2.6" fill="currentColor" />
    </svg>
  );
}

function StatusAlertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M12 4.2 20 18H4l8-13.8Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M12 9.4v4.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="12" cy="16.5" r="1" fill="currentColor" />
    </svg>
  );
}

function StatusCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
      <path d="m8.6 12.2 2.1 2.2 4.7-4.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

NotificationPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  notifications: PropTypes.array.isRequired,
  unreadCount: PropTypes.number,
  isLoading: PropTypes.bool,
  errorMessage: PropTypes.string,
  onClose: PropTypes.func,
  onClearAll: PropTypes.func,
  onNotificationClick: PropTypes.func,
};

NotificationGroup.propTypes = {
  title: PropTypes.string.isRequired,
  notifications: PropTypes.array.isRequired,
  onNotificationClick: PropTypes.func,
};

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    actorUsername: PropTypes.string,
    actorProfileImage: PropTypes.string,
    actionUrl: PropTypes.string,
    createdAt: PropTypes.string,
    isRead: PropTypes.bool,
  }).isRequired,
  onClick: PropTypes.func,
};

NotificationAvatar.propTypes = {
  username: PropTypes.string,
  profileImage: PropTypes.string,
};

export default NotificationPanel;
